import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Icon from '../../../components/AppIcon';
import { formatDate } from '../../../utils/formatters';

const ConferenceCard = ({ conference }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('conferences');
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const getStatusColor = (status) => {
    const statusMap = {
      'submission_open': 'bg-success/15 text-success border-success/30',
      'open': 'bg-success/15 text-success border-success/30',
      'submission_closed': 'bg-error/15 text-error border-error/30',
      'closed': 'bg-error/15 text-error border-error/30',
      'closing-soon': 'bg-warning/15 text-warning border-warning/30',
      'review_phase': 'bg-warning/15 text-warning border-warning/30',
      'completed': 'bg-muted/40 text-muted-foreground border-border',
      'published': 'bg-primary/15 text-primary border-primary/30'
    };
    return statusMap?.[status?.toLowerCase()] || 'bg-muted/40 text-muted-foreground border-border';
  };

  const fmt = (dateString) => (dateString ? formatDate(dateString) : t('publicBoard.card.tba'));

  const dateRange = (() => {
    if (conference?.startDate && conference?.endDate) {
      return `${fmt(conference?.startDate)} – ${fmt(conference?.endDate)}`;
    }
    if (conference?.startDate) return fmt(conference?.startDate);
    return t('publicBoard.card.datesTBA');
  })();

  const statusLabel = conference?.status
    ? conference?.status?.replace(/_/g, ' ')?.replace(/\b\w/g, (c) => c?.toUpperCase())
    : 'Draft';

  const showImage = conference?.bannerUrl && !imgError;

  const open = () => navigate(`/conference-landing-page?conferenceId=${conference?.id}`);

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      className="group relative flex flex-col bg-card border border-border overflow-hidden cursor-pointer transition-colors duration-200 hover:border-primary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      {/* Red rule that grows on hover (matches the landing feature cards) */}
      <span className="absolute top-0 ltr:left-0 rtl:right-0 z-10 h-[2px] w-0 bg-primary group-hover:w-full transition-all duration-500" />

      {/* Banner */}
      <div className="relative h-28 bg-gradient-to-br from-primary/15 via-secondary/10 to-primary/5 overflow-hidden">
        {showImage && (
          <>
            <img
              src={conference?.bannerUrl}
              alt={conference?.name}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
              loading="lazy"
              decoding="async"
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
            />
            {imgLoaded && <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-black/10" />}
          </>
        )}

        <span
          className={`absolute top-2.5 right-2.5 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border backdrop-blur-sm ${getStatusColor(conference?.status)}`}
        >
          {statusLabel}
        </span>

        <span className="absolute bottom-2.5 left-2.5 max-w-[70%] truncate text-[11px] font-medium px-2 py-0.5 bg-background/85 text-foreground border border-border backdrop-blur-sm">
          {conference?.subjectArea || t('publicBoard.card.general')}
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-4">
        <h3 className="text-[15px] font-bold leading-snug text-foreground line-clamp-2 group-hover:text-primary transition-colors">
          {conference?.name}
        </h3>

        <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">
          {conference?.description || t('publicBoard.card.noDescription')}
        </p>

        <div className="mt-3 space-y-1.5 text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="Calendar" size={14} className="text-primary shrink-0" />
            <span className="truncate">{dateRange}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="MapPin" size={14} className="text-primary shrink-0" />
            <span className="truncate">{conference?.location || t('publicBoard.card.locationTBA')}</span>
          </div>
        </div>

        {/* Footer: deadline + CTA */}
        <div className="mt-auto pt-3 flex items-center justify-between gap-2 border-t border-border/60">
          <div className="flex min-w-0 items-center gap-1.5 text-[11px] font-medium text-error">
            <Icon name="Clock" size={13} className="shrink-0" />
            <span className="truncate">
              {t('publicBoard.card.deadline')}: {fmt(conference?.paperDeadline)}
            </span>
          </div>
          <span
            aria-label={t('publicBoard.card.viewDetails')}
            className="inline-flex shrink-0 items-center justify-center w-7 h-7 border border-border text-primary transition-colors group-hover:bg-primary group-hover:border-primary group-hover:text-white rtl:rotate-180"
          >
            <Icon name="ArrowRight" size={15} />
          </span>
        </div>
      </div>
    </article>
  );
};

export default ConferenceCard;
